'use client';

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';

function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00:00';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export default function SupportAccessBanner() {
  const { data: session, update } = useSession();
  const router = useRouter();
  const [now, setNow] = useState(() => Date.now());
  const [exiting, setExiting] = useState(false);

  const grant = (session?.user as any)?.supportAccess as
    | { organizationId: string; organizationName: string; expiresAt: string }
    | undefined;

  const expiresAtMs = grant ? new Date(grant.expiresAt).getTime() : null;
  const msRemaining = expiresAtMs ? expiresAtMs - now : 0;

  const exitAccess = useCallback(async (organizationId: string) => {
    setExiting(true);
    try {
      await fetch(`/api/support/accounts/${organizationId}/access`, { method: 'DELETE' });
    } catch {
      // best-effort — still clear the local grant even if the audit-log write fails
    }
    await update({ supportAccessGrant: null });
    router.push(`/support/accounts/${organizationId}`);
  }, [update, router]);

  useEffect(() => {
    if (!grant) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [grant]);

  useEffect(() => {
    if (grant && expiresAtMs && expiresAtMs <= now && !exiting) {
      exitAccess(grant.organizationId);
    }
  }, [grant, expiresAtMs, now, exiting, exitAccess]);

  const countdown = useMemo(() => formatCountdown(msRemaining), [msRemaining]);

  if (!grant) return null;

  return (
    <div className="sticky top-0 z-50 flex items-center justify-between gap-4 bg-teal-600 px-4 py-2 text-sm font-medium text-white">
      <span>
        You are accessing <strong>{grant.organizationName}</strong> as support — expires in {countdown}
      </span>
      <Button
        onClick={() => exitAccess(grant.organizationId)}
        disabled={exiting}
        variant="ghost"
        className="h-auto rounded-md bg-white/15 px-3 py-1 font-semibold text-white hover:bg-white/25 hover:text-white"
      >
        {exiting ? 'Exiting...' : 'Exit support access'}
      </Button>
    </div>
  );
}
